import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgApexchartsModule } from 'ng-apexcharts';
import { ToastrService } from 'ngx-toastr';
import { LeadsCardComponent } from '../../../../shared/components/leads-card/leads-card.component';
import { MultiselectDropdownComponent } from '../../../../shared/components/multiselect-dropdown/multiselect-dropdown.component';
import { SalesInfoCardComponent } from '../../../../shared/components/sales-info-card/sales-info-card.component';
import {
  CHART_TITLES,
  DEFAULT_CHART_OPTIONS,
  SALES_TRACKER_SUMMARY_TITLE,
  STATS_CARD_DATA,
} from '../../../../shared/constants/sales-dashboard.constant';
import { ISalesInfoCardData } from '../../../../shared/interface/interface';
import {
  DropDownConfig,
  DropDownOption,
} from '../../../../shared/models/multiselect-dropdown.model';
import {
  BaseSummaryModel,
  OwnerSummaryModel,
  SalesDataModel,
} from '../../../../shared/models/sales-dashboard.model';
import { SalesDashboardService } from '../../../../shared/services/sales-dashboard.service';
import { StorageService } from '../../../../shared/services/storage.service';
import { SharedModule } from '../../../../shared/shared.module';
import { SummaryKeys } from '../../../../shared/types/sales-dashboard.type';
import { getNameInitials } from '../../../../shared/utils/utils';

@Component({
  selector: 'app-sales-dashboard',
  standalone: true,
  imports: [
    SharedModule,
    SalesInfoCardComponent,
    MultiselectDropdownComponent,
    NgApexchartsModule,
    LeadsCardComponent,
  ],
  templateUrl: './sales-dashboard.component.html',
  styleUrl: './sales-dashboard.component.scss',
})
export class SalesDashboardComponent implements OnInit {
  endDate = new Date();
  startDate = new Date();

  salesTrackerForm: FormGroup = this.fb.group({
    user: [{ value: 'All', label: 'All' }, []],
    dateFilter: this.fb.group({
      startDate: [''],
      endDate: [''],
    }),
  });

  salesCardData: { key: string; totalCount: string }[] | null = null;
  salesTrackerData: SalesDataModel | null = null;
  salesTrackerLoading: boolean = false;
  salesCardTitles = SALES_TRACKER_SUMMARY_TITLE;
  userDropdownSettings: DropDownConfig = {
    allowSearchFilter: false,
    title: 'Users',
    placeholder: 'Select User',
    searchPlaceholderText: '',
    allowPagination: false,
    required: false,
    valueField: 'value',
  };

  userOptions: DropDownOption[] = [];
  charts: any[] = [];

  //   static data
  stats: ISalesInfoCardData[] = STATS_CARD_DATA;
  totalLeads: number | string = 0;
  totalConversions: number | string = 0;
  constructor(
    private readonly fb: FormBuilder,
    private readonly salesDashboardService: SalesDashboardService,
    private readonly toastr: ToastrService,
    private readonly storageService: StorageService
  ) {}
  ngOnInit(): void {
    this.init();
  }

  init() {
    this.subscribeToDateFilterValueChanges();
    this.subscribeToUserValueChanges();
    this.setInitialDateFilterValues();
  }

  getSalesTrackerSummary(startDate?: string, endDate?: string) {
    this.salesTrackerLoading = true;
    this.salesDashboardService
      .fetchSalesTrackerSummary(startDate, endDate)
      .subscribe({
        next: (data: SalesDataModel) => {
          this.salesTrackerData = data;
          data.summaryByOwner?.forEach((el) => {
            el.nameInitial = getNameInitials(
              el.ownername,
              `-${el?.totalConversions}`
            );
          });
          this.setCardsData(data.summary);
          this.salesTrackerLoading = false;
          this.setUserOptionsValues(data.summaryByOwner);
          this.setChartData(data);
        },
        error: (err) => {
          this.salesTrackerLoading = false;

          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
        },
      });
  }

  subscribeToDateFilterValueChanges() {
    this.salesTrackerForm?.get('dateFilter')?.valueChanges.subscribe((data) => {
      this.getSalesTrackerSummary(data.startDate, data.endDate);
    });
  }

  setCardsData(data?: BaseSummaryModel) {
    if (data) {
      this.stats.forEach((el) => {
        el.value = data?.[el.identifier as SummaryKeys] ?? 0;
      });
      this.totalLeads = data?.totalleads ?? 0;
      this.totalConversions = data?.totalConversions ?? 0;
    }
  }

  setInitialDateFilterValues() {
    const today = new Date();
    const lastMonthDate = new Date();
    lastMonthDate.setMonth(new Date().getMonth() - 1);
    const startDate = lastMonthDate.toISOString().split('T')[0];
    const endDate = today.toISOString().split('T')[0];
    this.salesTrackerForm.patchValue({ dateFilter: { startDate, endDate } });
  }

  setUserOptionsValues(owners?: OwnerSummaryModel[]) {
    if (owners) {
      this.userOptions = [];
      this.userOptions.push({ value: 'All', label: 'All' });
      owners?.forEach((owner) =>
        this.userOptions.push({
          label: owner?.nameInitial ?? '',
          value: owner.owneremail,
        })
      );
      this.salesTrackerForm.patchValue({
        user: { value: 'All', label: 'All' },
      });
    }
  }

  subscribeToUserValueChanges() {
    this.salesTrackerForm?.get('user')?.valueChanges.subscribe((user) => {
      const selectedUser: any = {
        ...(this.salesTrackerData?.summaryByOwner?.find(
          (el) => el.owneremail === user.value
        ) ?? this.salesTrackerData?.summary),
      };
      if (selectedUser?.owneremail && selectedUser?.ownername) {
        delete selectedUser?.['owneremail'];
        delete selectedUser?.['ownername'];
      }
      this.setCardsData(selectedUser);
    });
  }

  setChartData(data: SalesDataModel) {
    const chartSeries: any = {
      leads: [] as { value: number; label: string }[],
      email: [] as { value: number; label: string }[],
      sms: [] as { value: number; label: string }[],
      call: [] as { value: number; label: string }[],
      totalConversions: [] as { value: number; label: string }[],
    };

    data.summaryByOwner?.forEach((el) => {
      chartSeries.call.push({
        value: +(el?.callcount ?? 0),
        label: el.nameInitial,
      });
      chartSeries.sms.push({
        value: +(el.smscount ?? 0),
        label: el?.nameInitial,
      });
      chartSeries.email.push({
        value: +(el?.emailcount ?? 0),
        label: el.nameInitial,
      });
      chartSeries.leads.push({
        value: +(el?.totalleads ?? 0),
        label: el.nameInitial,
      });
      chartSeries.totalConversions.push({
        value: +(el?.totalConversions ?? 0),
        label: el.nameInitial,
      });
    });
    this.charts = [];
    Object.keys(chartSeries).forEach((key) => {
      const obj = JSON.parse(JSON.stringify(DEFAULT_CHART_OPTIONS));
      obj.series = chartSeries?.[key].map((el: any) => el.value);
      obj.labels = chartSeries?.[key].map((el: any) => el.label);
      obj.title = {
        text: CHART_TITLES?.[key] ?? '',
        align: 'left',
      };
      this.charts.push(obj);
    });
  }
}
